// client/src/components/pages/LexiStar/Drivers/driverLogic.js
//
// Pure helpers for the driver register and driver–vehicle allocation (LS-09
// §5.9; FR-DRV-001/002/003/005, FR-NTF-011): channel and consent language,
// reachability, phone normalisation (the server's rule, for inline hints),
// allocation spans, the timeline layout and the "who was driving?" lookup.

export const DRIVER_CHANNELS = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'telegram', label: 'Telegram' },
  { value: 'sms', label: 'SMS' },
];

export const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'af', label: 'Afrikaans' },
];

export const CONSENT_METHODS = [
  { value: 'form', label: 'Signed form' },
  { value: 'verbal', label: 'Verbal (recorded by me)' },
  { value: 'import', label: 'Imported record' },
];

export const channelLabel = (c) =>
  ({
    whatsapp: 'WhatsApp',
    telegram: 'Telegram',
    sms: 'SMS',
    in_app: 'In-app',
    fake: 'Test (fake)',
  })[c] || c;

export const languageLabel = (l) => LANGUAGES.find((x) => x.value === l)?.label || l || '—';

/** The wording a driver agrees to (stored with the opt-in, FR-NTF-011). */
export function defaultConsentWording(channel, language = 'en', organisation = 'the transporter') {
  const ch = channelLabel(channel);
  if (language === 'af') {
    return `Ek stem in om ritboodskappe van ${organisation} op ${ch} te ontvang. Antwoord STOP om uit te teken.`;
  }
  return `I agree to receive trip messages from ${organisation} on ${ch}. Reply STOP to opt out.`;
}

/** '+27 82 123 4567', '0821234567', '0027…' → '+27821234567' (null when it cannot be one). */
export function normalisePhone(raw, countryCode = '27') {
  if (raw === null || raw === undefined) return null;
  let s = String(raw).trim();
  if (!s) return null;
  const plus = s.startsWith('+');
  s = s.replace(/[\s\-().]/g, '');
  if (plus) s = s.slice(1);
  if (!/^\d+$/.test(s)) return null;
  let digits;
  if (plus) digits = s;
  else if (s.startsWith('00')) digits = s.slice(2);
  else if (s.startsWith('0')) digits = countryCode + s.slice(1);
  else if (s.startsWith(countryCode) && s.length >= 11) digits = s;
  else return null;
  const e164 = `+${digits}`;
  return /^\+[1-9]\d{7,14}$/.test(e164) ? e164 : null;
}

/** Current consent state for a channel from the driver row's `consents` map. */
export function consentOf(driver, channel) {
  const c = driver?.consents?.[channel];
  if (!c) return null;
  return typeof c === 'string' ? { state: c } : c;
}

/**
 * Which channels can reach the driver now: WhatsApp and SMS need a number and
 * an opt-in; Telegram needs a linked chat (the driver started the bot).
 */
export function reachableChannels(driver) {
  const out = [];
  const optedIn = (c) => consentOf(driver, c)?.state === 'opted_in';
  const optedOut = (c) => consentOf(driver, c)?.state === 'opted_out';
  if (driver?.phone_e164 && optedIn('whatsapp')) out.push('whatsapp');
  if (driver?.telegram_chat_id && !optedOut('telegram')) out.push('telegram');
  if (driver?.phone_e164 && optedIn('sms')) out.push('sms');
  return out;
}

export const CONSENT_META = {
  opted_in: { label: 'Opted in', glyph: '✔', className: 'bg-emerald-50 text-emerald-800' },
  opted_out: { label: 'Opted out', glyph: '✖', className: 'bg-red-50 text-red-700' },
  none: { label: 'No record', glyph: '–', className: 'bg-slate-100 text-slate-600' },
};

// ── Allocations ────────────────────────────────────────────────────────────

const ms = (v) => (v ? Date.parse(v) : NaN);

/** Allocation row → + fromMs / toMs (Infinity when open-ended). */
export function spanOf(a) {
  const fromMs = ms(a.from ?? a.span_start ?? a.span?.from);
  const toRaw = a.to ?? a.span_end ?? a.span?.to ?? null;
  const toMs = toRaw ? ms(toRaw) : Infinity;
  return { fromMs, toMs };
}

export const isOpenEnded = (a) => spanOf(a).toMs === Infinity;

export function isCurrent(a, now = Date.now()) {
  if (a.cancelled) return false;
  const { fromMs, toMs } = spanOf(a);
  return fromMs <= now && now < toMs;
}

export function overlaps(a, b) {
  const x = spanOf(a);
  const y = spanOf(b);
  return x.fromMs < y.toMs && y.fromMs < x.toMs;
}

/**
 * Timeline geometry for allocations inside [start, end): left/width in % and
 * whether the bar runs past either edge.
 */
export function timelineBars(allocations, start, end) {
  const span = end - start;
  return allocations
    .filter((a) => !a.cancelled)
    .map((a) => {
      const { fromMs, toMs } = spanOf(a);
      if (!(toMs > start) || !(fromMs < end)) return null;
      const l = Math.max(fromMs, start);
      const r = Math.min(toMs, end);
      return {
        allocation: a,
        left: ((l - start) / span) * 100,
        width: Math.max(0.5, ((r - l) / span) * 100),
        clippedLeft: fromMs < start,
        clippedRight: toMs > end,
      };
    })
    .filter(Boolean);
}

/**
 * Stack timeline bars into lanes so overlapping spans stay visible; a bar that
 * shares time with another is flagged `overlap` (one driver on two trucks).
 */
export function laneBars(bars) {
  const sorted = [...bars].sort((a, b) => a.left - b.left);
  const laneEnds = [];
  const out = sorted.map((b) => {
    const right = b.left + b.width;
    let lane = laneEnds.findIndex((endPct) => endPct <= b.left + 1e-9);
    if (lane < 0) lane = laneEnds.length;
    laneEnds[lane] = right;
    return { ...b, lane };
  });
  return out.map((b) => ({
    ...b,
    overlap: out.some(
      (o) => o !== b && o.left < b.left + b.width - 1e-9 && b.left < o.left + o.width - 1e-9
    ),
  }));
}

/**
 * Drivers allocated to two assets at overlapping times inside the window —
 * the EXCLUDE constraint prevents two drivers on one asset, not one driver on
 * two assets, so that is the conflict the board highlights.
 */
export function doubleAllocatedDrivers(allocations) {
  const byDriver = new Map();
  allocations
    .filter((a) => !a.cancelled)
    .forEach((a) => {
      const list = byDriver.get(a.driver_id) || [];
      list.push(a);
      byDriver.set(a.driver_id, list);
    });
  const out = new Set();
  byDriver.forEach((list) => {
    for (let i = 0; i < list.length; i++) {
      for (let j = i + 1; j < list.length; j++) {
        if (list[i].asset_id !== list[j].asset_id && overlaps(list[i], list[j])) {
          out.add(list[i].id);
          out.add(list[j].id);
        }
      }
    }
  });
  return out;
}

/** "Who was driving?" — the allocation covering an instant for an asset (FR-DRV-003). */
export function whoWasDriving(allocations, assetId, atMs) {
  return (
    allocations.find((a) => {
      if (a.cancelled || String(a.asset_id) !== String(assetId)) return false;
      const { fromMs, toMs } = spanOf(a);
      return fromMs <= atMs && atMs < toMs;
    }) || null
  );
}

/** Evidence of an allocation lookup as CSV (one header row, one data row). */
export function evidenceCsv({ registration, at, allocation }) {
  const cells = [
    [
      'registration',
      'at',
      'driver',
      'driver_tag',
      'allocated_from',
      'allocated_to',
      'source',
      'recorded_by',
      'recorded_at',
      'allocation_id',
    ],
    [
      registration,
      at,
      allocation?.driver_name ?? '',
      allocation?.driver_tag ?? '',
      allocation?.from ?? '',
      allocation?.to ?? '',
      allocation?.source ?? '',
      allocation?.created_by ?? '',
      allocation?.created_at ?? '',
      allocation?.id ?? '',
    ],
  ];
  return cells
    .map((row) =>
      row
        .map((c) => (/[",\n]/.test(String(c)) ? `"${String(c).replace(/"/g, '""')}"` : String(c)))
        .join(',')
    )
    .join('\n');
}

/** First few lines of a CSV (for the import preview before the dry run). */
export const csvHead = (text, lines = 6) =>
  String(text || '')
    .split(/\r?\n/)
    .slice(0, lines)
    .join('\n');

export const CSV_TEMPLATE =
  'name,tag,language,phone,telegram_chat_id,preferred_channel,opt_in\n' +
  'Thabo Nkosi,EMP-104,en,082 123 4567,,whatsapp,whatsapp;sms\n' +
  'Pieter Botha,EMP-221,af,+27831234567,,telegram,\n';
